import React, { memo } from "react";
import { Link } from "react-router-dom";
import {
  Pencil, Trash2, Church, MapPinned, User, Mic,
  HeartHandshake, Cake, Clock, NotebookPen, Bell, Heart,
  AlarmClock,
} from "lucide-react";
import OfflineItemBadge from "../../../components/OfflineItemBadge";
import { getLocalDateString } from "@/lib/date";

export const AGENDA_TYPE_CONFIG = {
  campo:       { accent: "bg-indigo-500", chipBg: "bg-indigo-50 dark:bg-indigo-900/30",   chipText: "text-indigo-700 dark:text-indigo-400",  iconBg: "bg-indigo-50 dark:bg-indigo-900/30",   iconColor: "text-indigo-600 dark:text-indigo-400",  Icon: MapPinned     },
  iglesia:     { accent: "bg-blue-500",   chipBg: "bg-blue-50 dark:bg-blue-900/30",       chipText: "text-blue-700 dark:text-blue-400",      iconBg: "bg-blue-50 dark:bg-blue-900/30",       iconColor: "text-blue-600 dark:text-blue-400",      Icon: Church        },
  personal:    { accent: "bg-violet-500", chipBg: "bg-violet-50 dark:bg-violet-900/30",   chipText: "text-violet-700 dark:text-violet-400",  iconBg: "bg-violet-50 dark:bg-violet-900/30",   iconColor: "text-violet-600 dark:text-violet-400",  Icon: User          },
  predicacion: { accent: "bg-amber-500",  chipBg: "bg-amber-50 dark:bg-amber-900/30",     chipText: "text-amber-700 dark:text-amber-400",    iconBg: "bg-amber-50 dark:bg-amber-900/30",     iconColor: "text-amber-600 dark:text-amber-400",    Icon: Mic           },
  cumpleanos:  { accent: "bg-pink-500",   chipBg: "bg-pink-50 dark:bg-pink-900/30",       chipText: "text-pink-700 dark:text-pink-400",      iconBg: "bg-pink-50 dark:bg-pink-900/30",       iconColor: "text-pink-600 dark:text-pink-400",      Icon: Cake          },
  aniversario: { accent: "bg-rose-500",   chipBg: "bg-rose-50 dark:bg-rose-900/30",       chipText: "text-rose-700 dark:text-rose-400",      iconBg: "bg-rose-50 dark:bg-rose-900/30",       iconColor: "text-rose-600 dark:text-rose-400",      Icon: Heart         },
  visita:      { accent: "bg-emerald-500",chipBg: "bg-emerald-50 dark:bg-emerald-900/30", chipText: "text-emerald-700 dark:text-emerald-400",iconBg: "bg-emerald-50 dark:bg-emerald-900/30", iconColor: "text-emerald-600 dark:text-emerald-400",Icon: HeartHandshake },
  seguimiento: { accent: "bg-orange-500", chipBg: "bg-orange-50 dark:bg-orange-900/30",   chipText: "text-orange-700 dark:text-orange-400",  iconBg: "bg-orange-50 dark:bg-orange-900/30",   iconColor: "text-orange-600 dark:text-orange-400",  Icon: Bell          },
  nota:        { accent: "bg-slate-400",  chipBg: "bg-slate-100 dark:bg-slate-700/60",    chipText: "text-slate-600 dark:text-slate-400",    iconBg: "bg-slate-100 dark:bg-slate-700/60",    iconColor: "text-slate-500 dark:text-slate-400",    Icon: NotebookPen   },
};

const DAYS_SHORT   = ["dom","lun","mar","mié","jue","vie","sáb"];
const MONTHS_SHORT = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];

// "mar 3 jun" or "mar 3 jun 2027" when year differs
function formatExactDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const yearSuffix = d.getFullYear() !== new Date().getFullYear() ? ` ${d.getFullYear()}` : "";
  return `${DAYS_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}${yearSuffix}`;
}

function formatDateRange(start, end) {
  if (!end || end === start) return null;
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()}–${e.getDate()} ${MONTHS_SHORT[e.getMonth()]} ${e.getFullYear()}`;
  }
  return `${s.getDate()} ${MONTHS_SHORT[s.getMonth()]} – ${e.getDate()} ${MONTHS_SHORT[e.getMonth()]} ${e.getFullYear()}`;
}

function getRelativeChip(date, endDate) {
  if (!date) return null;
  const today = getLocalDateString();
  if (endDate && endDate > date && today >= date && today <= endDate) {
    return { label: "En curso", bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-700 dark:text-emerald-400" };
  }
  const daysAway = Math.round(
    (new Date(date + "T00:00:00") - new Date(today + "T00:00:00")) / 86_400_000
  );
  if (daysAway === 0)          return { label: "Hoy",          bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-700 dark:text-emerald-400" };
  if (daysAway === 1)          return { label: "Mañana",        bg: "bg-blue-100 dark:bg-blue-900/30",       text: "text-blue-700 dark:text-blue-400"       };
  if (daysAway === -1)         return { label: "Ayer",          bg: "bg-orange-100 dark:bg-orange-900/30",   text: "text-orange-700 dark:text-orange-400"   };
  if (daysAway > 1 && daysAway <= 6)
                               return { label: `En ${daysAway} días`, bg: "bg-slate-100 dark:bg-slate-700/60", text: "text-slate-600 dark:text-slate-400" };
  if (daysAway < -1)           return { label: "Pasado",        bg: "bg-red-50 dark:bg-red-900/20",          text: "text-red-500 dark:text-red-400"         };
  return null;
}

const AgendaEventCard = memo(function AgendaEventCard({
  href,
  title,
  date,
  endDate,
  time,
  subtitle,
  contextLine,
  type = "personal",
  typeLabel,
  allDay = false,
  done = false,
  overdue = false,
  onEdit,
  onDelete,
  testId,
  item,
}) {
  const cfg       = AGENDA_TYPE_CONFIG[type] || AGENDA_TYPE_CONFIG.personal;
  const Icon      = cfg.Icon;
  const relative  = getRelativeChip(date, endDate);
  const dateRange = formatDateRange(date, endDate);
  const isPast   = overdue || (done && date && date < getLocalDateString());

  // Show exact date for events 2+ days away where the relative chip
  // doesn't convey the specific day ("Esta semana", "Próxima semana", "Este mes", "Más adelante").
  const _today    = getLocalDateString();
  const _daysAway = date ? Math.round((new Date(date + "T00:00:00") - new Date(_today + "T00:00:00")) / 86_400_000) : null;
  const exactDate = _daysAway !== null && _daysAway >= 2 && !dateRange ? formatExactDate(date) : null;
  const showTime = !allDay && time;

  return (
    <Link
      to={href || "#"}
      className={`
        list-item-lazy relative flex items-center bg-white dark:bg-slate-800 rounded-[24px] border border-slate-200/50 dark:border-slate-700/60
        shadow-[0_2px_12px_rgba(0,0,0,0.03)] overflow-hidden
        transition-all duration-300 ease-out
        hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)]
        active:scale-[0.98] active:opacity-80
        ${isPast ? "opacity-60" : ""}
      `}
      data-testid={testId}
    >
      {/* Left accent rail */}
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${cfg.accent}`} />

      {/* Content */}
      <div className="flex items-center gap-3 flex-1 min-w-0 pl-4 py-3 pr-1">
        {/* Icon container */}
        <div className={`h-11 w-11 rounded-[14px] flex items-center justify-center shrink-0 ${cfg.iconBg}`}>
          <Icon size={18} className={cfg.iconColor} strokeWidth={2} />
        </div>

        <div className="flex-1 min-w-0">
          {/* Title — allow up to 2 lines */}
          <p className={`
            text-[14px] font-semibold leading-snug
            ${done ? "line-through text-slate-400 dark:text-slate-600" : "text-slate-900 dark:text-slate-100"}
          `}
            style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
          >
            {title}
          </p>

          {contextLine && (
            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 truncate leading-none">
              {contextLine}
            </p>
          )}

          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {/* Time or all-day */}
            {showTime && (
              <span className="flex items-center gap-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                <Clock size={10} className="shrink-0" />
                {time.slice(0, 5)}
              </span>
            )}
            {allDay && !time && (
              <span className="flex items-center gap-0.5 text-[11px] text-slate-400 dark:text-slate-500">
                <AlarmClock size={10} className="shrink-0" />
                Todo el día
              </span>
            )}

            {/* Relative chip */}
            {relative && (
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${relative.bg} ${relative.text}`}>
                {relative.label}
              </span>
            )}

            {/* Exact date — shown for events 2+ days away so the pastor sees the specific day */}
            {exactDate && (
              <span className="text-[11px] text-slate-500 dark:text-slate-400">{exactDate}</span>
            )}

            {/* Type label chip */}
            {typeLabel && (
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.chipBg} ${cfg.chipText}`}>
                {typeLabel}
              </span>
            )}

            {/* Date range for multiday events */}
            {dateRange && (
              <span className="text-[11px] text-slate-400 dark:text-slate-500">{dateRange}</span>
            )}

            {/* Subtitle */}
            {subtitle && (
              <span className="text-[11px] text-slate-400 dark:text-slate-500 truncate">{subtitle}</span>
            )}

            <OfflineItemBadge item={item} />
          </div>
        </div>
      </div>

      {/* Actions */}
      {(onEdit || onDelete) && (
        <div className="flex items-center gap-0.5 pr-2 shrink-0">
          {onEdit && (
            <button
              onClick={(e) => { e.preventDefault(); onEdit(); }}
              className="h-11 w-11 rounded-[14px] text-slate-400 dark:text-slate-500 hover:text-brand hover:bg-brand-light dark:hover:bg-brand/20 flex items-center justify-center transition-all duration-200"
              aria-label="Editar"
            >
              <Pencil size={13} />
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => { e.preventDefault(); onDelete(); }}
              className="h-11 w-11 rounded-[14px] text-slate-400 dark:text-slate-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center transition-all duration-200"
              aria-label="Eliminar"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      )}
    </Link>
  );
});

export default AgendaEventCard;
